import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';

import { ReportShiftActions, ReportShiftActionTypes, TourActions } from '../actions';
import { ShiftBreak, TourStopTracking } from '@models/*';

export interface ReportShiftState {
    entities: { [id: number]: any };
    loaded: boolean;
    loading: string[];
    selectedDay: string;
    updatesLoading: string[];
}

// todo add reportShiftInterface instead of any

export const initState: ReportShiftState = {
    entities: {},
    loaded: false,
    loading: [],
    selectedDay: '',
    updatesLoading: []
};

export function reportShiftReducer(
    state = initState,
    action: ReportShiftActions | TourActions
): ReportShiftState {

    switch (action.type) {
        case ReportShiftActionTypes.SELECT_REPORT_DAY: {
            return {
                ...state,
                selectedDay: action.payload
            };
        }

        case ReportShiftActionTypes.LOAD_REPORT_SHIFT: {
            return {
                ...state,
                loading: [...state.loading, action.payload]
            };
        }

        case ReportShiftActionTypes.LOAD_REPORT_SHIFT_SUCCESS: {
            let entities;

            if (action.payload.reportShift.length) {
                entities = {
                    ...state.entities,
                    [action.payload.date]: action.payload.reportShift
                };
            } else {
                entities = { ...state.entities };
            }

            return {
                ...state,
                loading: [...state.loading.filter(date => date !== action.payload.date)],
                loaded: true,
                entities
            };
        }

        case ReportShiftActionTypes.LOAD_REPORT_SHIFT_FAIL: {
            return {
                ...state,
                loading: [...state.loading.filter(date => date !== action.payload.date)],
                loaded: false
            };
        }

        case ReportShiftActionTypes.START_SHIFT:
        case ReportShiftActionTypes.END_SHIFT:
        case ReportShiftActionTypes.START_SHIFT_BREAK:
        case ReportShiftActionTypes.END_SHIFT_BREAK:
        case ReportShiftActionTypes.START_TOUR_STOP_TRACKING:
        case ReportShiftActionTypes.END_TOUR_STOP_TRACKING:
        case ReportShiftActionTypes.EDIT_SHIFT:
        case ReportShiftActionTypes.EDIT_SHIFT_BREAK:
        case ReportShiftActionTypes.EDIT_TOUR_STOP_TRACKING: {
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);

            return {
                ...state,
                updatesLoading: [...state.updatesLoading, lookupDate]
            };
        }

        case ReportShiftActionTypes.START_SHIFT_FAIL:
        case ReportShiftActionTypes.END_SHIFT_FAIL:
        case ReportShiftActionTypes.START_SHIFT_BREAK_FAIL:
        case ReportShiftActionTypes.END_SHIFT_BREAK_FAIL:
        case ReportShiftActionTypes.START_TOUR_STOP_TRACKING_FAIL:
        case ReportShiftActionTypes.END_TOUR_STOP_TRACKING_FAIL:
        case ReportShiftActionTypes.EDIT_SHIFT_FAIL:
        case ReportShiftActionTypes.EDIT_SHIFT_BREAK_FAIL:
        case ReportShiftActionTypes.EDIT_TOUR_STOP_TRACKING_FAIL:
        case ReportShiftActionTypes.CANCEL_SHIFT_UPDATE: {
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);

            return {
                ...state,
                updatesLoading: [...state.updatesLoading.filter(date => date !== lookupDate)],
            };
        }

        case ReportShiftActionTypes.START_SHIFT_SUCCESS: {
            const newShift = {
                id: action.payload.shiftId,
                startedAt: action.payload.date,
                endedAt: null,
                status: 'start',
                tourStopTracking: [],
                tourStopTrackings: [],
                shiftBreaks: [],
                registeredKmStart: action.payload.registeredKmStart,
                registeredKmEnd: null,
                nightStay: null,
                edited: false
            };

            let entities = cloneDeep(state.entities);
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);

            if (!entities.hasOwnProperty(lookupDate)) {
                entities = {
                    ...entities,
                    [lookupDate]: []
                };
            }

            entities[lookupDate].push(newShift);

            return {
                ...state,
                updatesLoading: [...state.updatesLoading.filter(date => date !== lookupDate)],
                entities
            };
        }

        case ReportShiftActionTypes.END_SHIFT_SUCCESS: {
            const entities = cloneDeep(state.entities);
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);
            const currShift = entities[lookupDate].find(shift => shift.id === action.payload.shiftId);

            currShift.endedAt = action.payload.date;
            currShift.status = 'end';
            currShift.nightStay = action.payload.nightStay;
            currShift.registeredKmEnd = action.payload.registeredKmEnd;

            return {
                ...state,
                updatesLoading: [...state.updatesLoading.filter(date => date !== lookupDate)],
                entities
            };
        }

        case ReportShiftActionTypes.START_SHIFT_BREAK_SUCCESS: {
            const newShiftBreak: ShiftBreak = {
                id: action.payload.breakId,
                shiftId: action.payload.shiftId,
                status: 'start',
                startedAt: action.payload.date,
                endedAt: null,
                edited: false
            };

            const entities = cloneDeep(state.entities);
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);
            const currShift = entities[lookupDate].find(shift => shift.id === action.payload.shiftId);

            currShift.shiftBreaks.push(newShiftBreak);

            return {
                ...state,
                updatesLoading: [...state.updatesLoading.filter(date => date !== lookupDate)],
                entities
            };
        }

        case ReportShiftActionTypes.END_SHIFT_BREAK_SUCCESS: {
            const entities = cloneDeep(state.entities);
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);
            const currShift = entities[lookupDate].find(shift => shift.id === action.payload.shiftId);
            const currBreak = currShift.shiftBreaks.find(shiftBreak => shiftBreak.id === action.payload.breakId);

            // TODO: avoid deep clone and make immutable update with only clone & change what is needed
            currBreak.endedAt = action.payload.date;
            currBreak.status = 'end';

            return {
                ...state,
                updatesLoading: [...state.updatesLoading.filter(date => date !== lookupDate)],
                entities
            };
        }

        case ReportShiftActionTypes.START_TOUR_STOP_TRACKING_SUCCESS: {
            const newTourStopTracking: TourStopTracking = {
                id: action.payload.tourStopTrackingId,
                shiftId: action.payload.shiftId,
                tourId: action.payload.tourId,
                tour: action.payload.tour,
                locationGeo: action.payload.locationGeo,
                locationType: action.payload.locationType,
                address: action.payload.address,
                city: action.payload.city,
                sitename: action.payload.sitename,
                startedAt: action.payload.date,
                endedAt: null,
                status: 'start',
                edited: false
            };

            const entities = cloneDeep(state.entities);
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);
            const currShift = entities[lookupDate].find(shift => shift.id === action.payload.shiftId);
            if (currShift) {
                currShift.tourStopTrackings.push(newTourStopTracking);
            }

            return {
                ...state,
                updatesLoading: [...state.updatesLoading.filter(date => date !== lookupDate)],
                entities
            };
        }

        case ReportShiftActionTypes.END_TOUR_STOP_TRACKING_SUCCESS: {
            const entities = cloneDeep(state.entities);
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);
            const currShift = entities[lookupDate].find(shift => shift.id === action.payload.shiftId);
            const currTourStopTracking = currShift.tourStopTrackings.find(tourStopTracking =>
                tourStopTracking.id === action.payload.tourStopTrackingId);

            currTourStopTracking.endedAt = action.payload.date;
            currTourStopTracking.status = 'end';

            return {
                ...state,
                updatesLoading: [...state.updatesLoading.filter(date => date !== lookupDate)],
                entities
            };
        }

        case ReportShiftActionTypes.EDIT_SHIFT_SUCCESS: {
            const entities = cloneDeep(state.entities);
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);
            const currShift = entities[lookupDate].find(shift => shift.id === action.payload.editedData.shiftId);

            currShift.startedAt = action.payload.editedData.startedAt || currShift.startedAt;
            currShift.endedAt = action.payload.editedData.endedAt || currShift.endedAt;
            currShift.registeredKmStart = action.payload.editedData.registeredKmStart || currShift.registeredKmStart;
            currShift.registeredKmEnd = action.payload.editedData.registeredKmEnd || currShift.registeredKmEnd;
            currShift.edited = true;

            return {
                ...state,
                updatesLoading: [...state.updatesLoading.filter(date => date !== lookupDate)],
                entities
            };
        }

        case ReportShiftActionTypes.EDIT_SHIFT_BREAK_SUCCESS: {
            const entities = cloneDeep(state.entities);
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);
            const currShift = entities[lookupDate].find(shift => shift.id === action.payload.shiftId);
            const currBreak = currShift.shiftBreaks.find(shiftBreak => shiftBreak.id === action.payload.editedData.shiftBreakId);

            currBreak.startedAt = action.payload.editedData.startedAt || currBreak.startedAt;
            currBreak.endedAt = action.payload.editedData.endedAt || currBreak.endedAt;
            currBreak.edited = true;

            return {
                ...state,
                updatesLoading: [...state.updatesLoading.filter(date => date !== lookupDate)],
                entities
            };
        }

        case ReportShiftActionTypes.EDIT_TOUR_STOP_TRACKING_SUCCESS: {
            const entities = cloneDeep(state.entities);
            const lookupDate = moment(action.payload.date).startOf('day').toISOString(true);
            const currShift = entities[lookupDate].find(shift => shift.id === action.payload.shiftId);
            const currTourStopTracking = currShift.tourStopTrackings.find(tourStopTracking =>
                tourStopTracking.id === action.payload.editedData.tourStopTrackingId);

            currTourStopTracking.startedAt = action.payload.editedData.startedAt || currTourStopTracking.startedAt;
            currTourStopTracking.endedAt = action.payload.editedData.endedAt || currTourStopTracking.endedAt;
            currTourStopTracking.edited = true;

            return {
                ...state,
                updatesLoading: [...state.updatesLoading.filter(date => date !== lookupDate)],
                entities
            };
        }

        case ReportShiftActionTypes.ADD_TRACKED_ACTIONS: {
            const entities = cloneDeep(state.entities);
            const now = moment().startOf('day').toISOString(true);
            const currShift = entities[now].find(shift => shift.id === action.payload.shiftId);
            const currTourStopTracking = currShift.tourStopTrackings.findIndex(tourStopTracking => tourStopTracking.id === action.payload.id);

            if (currTourStopTracking !== -1) {
                currShift.tourStopTrackings[currTourStopTracking] = action.payload;
            } else {
                currShift.tourStopTrackings.push(currTourStopTracking);
            }

            return {
                ...state,
                entities
            };
        }
    }

    return state;
}
